/**
 *  Pool Widget rows parser to get:
 *  - cex rows
 *  - dex rows
 *  - score rows
 *  - GoPlus Security rows
 */
import {
  ICoinWithMarket,
  IOrganicTradesInfo,
  ITicker,
  ITokenInfo,
  ITokenScore,
} from '@kattana-io/common';
import { ITickerLivePrice } from '../../Watchlist/hooks/useTickerLivePrice';
import { getPriceChange } from '../../../helpers/priceChange';
import {
  getIssueColor,
  getPriceChangeColor,
  getPriceChangePercentColor,
} from './colors';
import { countIssues } from './calculations';
import { ITickerDisplayData } from '../../../hooks/useTicker';
import { IGoPlusTokenData } from '../../../helpers/goPlusApi';

export type PoolWidgetRow =
  | {
      id: string;
      label: string;
      value: number;
      sign: string;
      color: string;
      tooltip?: string;
      type: 'price';
    }
  | {
      id: string;
      label: string;
      value: number;
      percent?: boolean;
      tooltip?: string;
      type: 'number' | 'usd' | 'date';
    }
  | {
      id: string;
      label: string;
      value: number;
      color: string;
      disabled: boolean;
      tooltip?: string;
      type: 'issue';
    }
  | {
      id: string;
      label: string;
      value: string;
      tooltip?: string;
      type: 'ticker_address' | 'coin_address' | 'wallet_address' | 'string';
    }
  | {
      id: string;
      label: string;
      value: number;
      percent: number;
      color: string;
      tooltip?: string;
      type: 'change';
    }
  | {
      id: string;
      label: string;
      value: boolean;
      tooltip?: string;
      type: 'boolean';
    };

const getCexPoolRows = (
  item: ITicker,
  priceInfo: ITickerLivePrice,
  coinInfo: ICoinWithMarket | null
): PoolWidgetRow[] => {
  const priceChangeInfo = getPriceChange(priceInfo.price.rawPrice, item);

  return [
    {
      id: `last-price-${item.id}`,
      label: 'Last price',
      value: priceInfo.price.rawPrice,
      sign: priceInfo.price.sign,
      color: getPriceChangeColor(priceInfo.side),
      type: 'price',
    },
    {
      id: `24h-${item.id}`,
      label: '24H',
      value: priceChangeInfo.priceChange,
      percent: priceChangeInfo.priceChangePercent,
      color: getPriceChangePercentColor(priceChangeInfo.priceChangePercent),
      type: 'change',
    },

    {
      id: `total-supply-${item.id}`,
      label: 'Total Supply',
      value: coinInfo?.market?.total_supply.toLocaleString('en') || '0',
      type: 'string',
    },

    {
      id: `circ-supply-${item.id}`,
      label: 'Circ. Supply',
      value: coinInfo?.market?.circulating_supply.toLocaleString('en') || '0',
      type: 'string',
    },
    {
      id: `owner_percent-${item.id}`,
      label: 'Circ. Supply in %',
      value:
        ((coinInfo?.market?.circulating_supply ?? 0) /
          (coinInfo?.market?.total_supply || 1)) *
        100,
      percent: true,
      type: 'number',
    },
    {
      id: `diluted-valuation-${item.id}`,
      label: 'Fully diluted valuation',
      value:
        (coinInfo?.market?.total_supply || 0) * (coinInfo?.market?.price || 0),
      type: 'usd',
    },
    {
      id: `market-cap-${item.id}`,
      label: 'Market Cap',
      value: coinInfo?.market?.market_cap || 0,
      type: 'usd',
    },
  ];
};

const getDexPoolRows = (
  item: ITicker,
  ticker: ITickerDisplayData,
  priceInfo: ITickerLivePrice,
  coinInfo: ITokenInfo | null,
  securityTokenInfo: IGoPlusTokenData | null,
  organicTradesInfo: IOrganicTradesInfo | null
): PoolWidgetRow[] => {
  const issuesCount = countIssues(securityTokenInfo);
  const priceChangeInfo = getPriceChange(priceInfo.price.rawPrice, item);

  const organicTradesRows: PoolWidgetRow[] = [];
  const holdersRows: PoolWidgetRow[] = [];

  if (organicTradesInfo) {
    organicTradesRows.push({
      id: `24-organic-volume-${item.id}`,
      label: '24H Organic volume:',
      value: organicTradesInfo.total_value_usd,
      type: 'usd',
    });
    organicTradesRows.push({
      id: `traders-bots-${item.id}`,
      label: 'Traders',
      value: organicTradesInfo.traders.toLocaleString('en'),
      type: 'string',
    });
  }

  if (coinInfo?.holders_count) {
    holdersRows.push({
      id: `holders-count-${item.id}`,
      label: `Holders ${ticker.base}:`,
      value: coinInfo.holders_count.toLocaleString('en'),
      type: 'string',
    });
  }

  const rows: PoolWidgetRow[] = [
    {
      id: `last-price-${item.id}`,
      label: 'Last price',
      value: priceInfo.price.rawPrice,
      sign: priceInfo.price.sign,
      color: getPriceChangeColor(priceInfo.side),
      type: 'price',
    },
    {
      id: `24h-${item.id}`,
      label: '24H',
      value: priceChangeInfo.priceChange,
      percent: priceChangeInfo.priceChangePercent,
      color: getPriceChangePercentColor(priceChangeInfo.priceChangePercent),
      type: 'change',
    },
    {
      id: `24h-volume-${item.id}`,
      label: '24H Volume',
      value: item.volume,
      type: 'usd',
    },
    ...organicTradesRows,
    ...holdersRows,
    {
      id: `pool-${item.id}`,
      label: 'Pool',
      value: item.address,
      type: 'ticker_address',
    },
    {
      id: `base-${item.id}`,
      label: ticker.base,
      value: ticker.base_address,
      type: 'coin_address',
    },
    {
      id: `quote-${item.id}`,
      label: ticker.quote,
      value: ticker.quote_address,
      type: 'coin_address',
    },
    {
      id: `pool-created-${item.id}`,
      label: 'Pool created:',
      value: item.pool_created,
      type: 'date',
    },
    {
      id: `pool-liquidity-${item.id}`,
      label: 'Pool liquidity:',
      value: item.liquidity,
      type: 'usd',
    },
    {
      id: `pool-base-${item.id}`,
      label: `Pooled ${ticker.base}:`,
      value: ticker.base_reserve.toLocaleString('en'),
      type: 'string',
    },
    {
      id: `pool-quote-${item.id}`,
      label: `Pooled ${ticker.quote}:`,
      value: ticker.quote_reserve.toLocaleString('en'),
      type: 'string',
    },
    {
      id: `issue-${item.id}`,
      label: 'Go+ security',
      value: issuesCount,
      color: getIssueColor(issuesCount, !securityTokenInfo),
      disabled: !securityTokenInfo,
      type: 'issue',
    },
  ];

  return rows;
};

const getScoreRows = (score: ITokenScore): PoolWidgetRow[] => [
  {
    id: 'coingecko',
    label: 'Coingecko',
    value: score.coingecko,
    type: 'boolean',
  },
  {
    id: 'dex_networks',
    label: 'More than 1 network',
    value: score.dex_networks,
    type: 'boolean',
  },
  {
    id: 'holders',
    label: 'More than 100 holders',
    value: score.holders,
    type: 'boolean',
  },
  {
    id: 'liquidity',
    label: 'More than 100000$ liquidity',
    value: score.liquidity,
    type: 'boolean',
  },
  {
    id: 'pairs',
    label: 'More than 1 pair',
    value: score.pairs,
    type: 'boolean',
  },
  {
    id: 'proxy',
    label: 'Is proxy contract',
    value: score.proxy,
    type: 'boolean',
  },
  {
    id: 'coinmarketcap',
    label: 'CoinMarketCap',
    value: score.coinmarketcap,
    type: 'boolean',
  },
  {
    id: 'old',
    label: 'Old',
    value: score.old,
    type: 'boolean',
  },
  {
    id: 'sellable',
    label: 'Sellable',
    value: score.sellable,
    type: 'boolean',
  },
  {
    id: 'trustwallet',
    label: 'Trustwallet',
    value: score.trustwallet,
    type: 'boolean',
  },
  {
    id: 'twitter',
    label: 'Twitter',
    value: score.twitter,
    type: 'boolean',
  },
];

const getGoPlusSecurityRows = (
  data: IGoPlusTokenData | null
): PoolWidgetRow[] => {
  if (!data) {
    return [];
  }

  const rows: PoolWidgetRow[] = [];

  const toBoolean = (value: '0' | '1') => value === '1';

  // Info Security Mappings
  if (data.token_name) {
    rows.push({
      id: 'token_name',
      label: 'Token Name',
      value: data.token_name,
      type: 'string',
      tooltip: 'The official name of the token.',
    });
  }

  if (data.token_symbol) {
    rows.push({
      id: 'token_symbol',
      label: 'Token Symbol',
      value: data.token_symbol,
      type: 'string',
      tooltip: 'The official symbol or abbreviation of the token.',
    });
  }

  if (data.total_supply) {
    rows.push({
      id: 'total_supply',
      label: 'Token Total Supply',
      value: Number(data.total_supply),
      type: 'number',
      tooltip: 'Total amount of tokens in existence.',
    });
  }

  if (data.owner_balance) {
    rows.push({
      id: 'owner_balance',
      label: 'Owner Balance',
      value: Number(data.owner_balance),
      type: 'number',
      tooltip: 'Total balance in tokens held by the contract owner.',
    });
  }

  if (data.owner_percent) {
    rows.push({
      id: 'owner_percent',
      label: 'Token Percentage of Owner',
      value: Number(data.owner_percent) * 100,
      percent: true,
      type: 'number',
      tooltip: 'Percentage of total tokens held by the contract owner.',
    });
  }

  if (data.creator_balance) {
    rows.push({
      id: 'creator_address',
      label: 'Creator Address',
      value: data.creator_address,
      type: 'wallet_address',
      tooltip:
        'Ethereum address of the individual or entity that created the token contract.',
    });
  }

  if (data.creator_balance) {
    rows.push({
      id: 'creator_balance',
      label: 'Creator Balance',
      value: Number(data.creator_balance),
      type: 'number',
      tooltip: 'Total balance in tokens held by the contract creator.',
    });
  }

  if (data.creator_percent) {
    rows.push({
      id: 'creator_percent',
      label: 'Token Percentage of Creator',
      value: Number(data.creator_percent) * 100,
      type: 'number',
      percent: true,
      tooltip: 'Percentage of total tokens held by the contract creator.',
    });
  }

  // Trading Security Mappings
  if (data.buy_tax) {
    rows.push({
      id: 'buy_tax',
      label: 'Buy Tax',
      value: Number(data.buy_tax) * 100,
      type: 'number',
      percent: true,
      tooltip:
        'Tax imposed on buying transactions, usually expressed as a percentage of the transaction amount.',
    });
  }

  if (data.sell_tax) {
    rows.push({
      id: 'sell_tax',
      label: 'Sell Tax',
      value: Number(data.sell_tax) * 100,
      type: 'number',
      percent: true,
      tooltip:
        'Tax imposed on selling transactions, usually expressed as a percentage of the transaction amount.',
    });
  }

  if (data.anti_whale_modifiable) {
    rows.push({
      id: 'anti_whale_modifiable',
      label: 'Anti-Whale Modifiable',
      value: toBoolean(data.anti_whale_modifiable),
      type: 'boolean',
      tooltip: 'Indicates if the anti-whale mechanism can be modified.',
    });
  }

  if (data.can_take_back_ownership) {
    rows.push({
      id: 'can_take_back_ownership',
      label: 'Can Take Back Ownership',
      value: toBoolean(data.can_take_back_ownership),
      type: 'boolean',
      tooltip: 'Indicates if the ownership can be taken back.',
    });
  }

  if (data.cannot_buy) {
    rows.push({
      id: 'cannot_buy',
      label: 'Cannot Buy',
      value: toBoolean(data.cannot_buy),
      type: 'boolean',
      tooltip: 'Indicates if buying is prohibited.',
    });
  }

  if (data.cannot_sell_all) {
    rows.push({
      id: 'cannot_sell_all',
      label: 'Cannot Sell All',
      value: toBoolean(data.cannot_sell_all),
      type: 'boolean',
      tooltip: 'Indicates if selling the entire amount is prohibited.',
    });
  }

  if (data.external_call) {
    rows.push({
      id: 'external_call',
      label: 'External Call',
      value: toBoolean(data.external_call),
      type: 'boolean',
      tooltip: 'Indicates if external contract calls are allowed.',
    });
  }

  if (data.hidden_owner) {
    rows.push({
      id: 'hidden_owner',
      label: 'Hidden Owner',
      value: toBoolean(data.hidden_owner),
      type: 'boolean',
      tooltip: 'Indicates if the ownership of the contract is hidden.',
    });
  }

  if (data.honeypot_with_same_creator) {
    rows.push({
      id: 'honeypot_with_same_creator',
      label: 'Honeypot with Same Creator',
      value: toBoolean(data.honeypot_with_same_creator),
      type: 'boolean',
      tooltip:
        'Indicates if there are any honeypots created by the same creator.',
    });
  }

  if (data.is_anti_whale) {
    rows.push({
      id: 'is_anti_whale',
      label: 'Is Anti-Whale',
      value: toBoolean(data.is_anti_whale),
      type: 'boolean',
      tooltip: 'Indicates if there is an anti-whale mechanism in place.',
    });
  }

  if (data.is_blacklisted) {
    rows.push({
      id: 'is_blacklisted',
      label: 'Is Blacklisted',
      value: toBoolean(data.is_blacklisted),
      type: 'boolean',
      tooltip: 'Indicates if the token is blacklisted.',
    });
  }

  if (data.is_honeypot) {
    rows.push({
      id: 'is_honeypot',
      label: 'Is Honeypot',
      value: toBoolean(data.is_honeypot),
      type: 'boolean',
      tooltip: 'Indicates if the contract is a honeypot.',
    });
  }

  if (data.is_in_dex) {
    rows.push({
      id: 'is_in_dex',
      label: 'Is In DEX',
      value: toBoolean(data.is_in_dex),
      type: 'boolean',
      tooltip: 'Indicates if the token is listed in a decentralized exchange.',
    });
  }

  if (data.is_proxy) {
    rows.push({
      id: 'is_proxy',
      label: 'Is Proxy',
      value: toBoolean(data.is_proxy),
      type: 'boolean',
      tooltip: 'Indicates if the contract acts as a proxy.',
    });
  }

  if (data.personal_slippage_modifiable) {
    rows.push({
      id: 'personal_slippage_modifiable',
      label: 'Personal Slippage Modifiable',
      value: toBoolean(data.personal_slippage_modifiable),
      type: 'boolean',
      tooltip: 'Indicates if a user can modify their slippage.',
    });
  }

  if (data.selfdestruct) {
    rows.push({
      id: 'selfdestruct',
      label: 'Self Destruct',
      value: toBoolean(data.selfdestruct),
      type: 'boolean',
      tooltip: 'Indicates if the contract can be self-destructed.',
    });
  }

  if (data.slippage_modifiable) {
    rows.push({
      id: 'slippage_modifiable',
      label: 'Slippage Modifiable',
      value: toBoolean(data.slippage_modifiable),
      type: 'boolean',
      tooltip: 'Indicates if the slippage rate is modifiable.',
    });
  }

  if (data.trading_cooldown) {
    rows.push({
      id: 'trading_cooldown',
      label: 'Trading Cooldown',
      value: toBoolean(data.trading_cooldown),
      type: 'boolean',
      tooltip: 'Indicates if there is a cooldown period between trades.',
    });
  }

  if (data.transfer_pausable) {
    rows.push({
      id: 'transfer_pausable',
      label: 'Transfer Pausable',
      value: toBoolean(data.transfer_pausable),
      type: 'boolean',
      tooltip: 'Indicates if token transfers can be paused.',
    });
  }

  if (data.is_true_token) {
    rows.push({
      id: 'is_true_token',
      label: 'True/Fake Token',
      value: toBoolean(data.is_true_token),
      type: 'boolean',
      tooltip: 'Indicates whether the token is genuine or a counterfeit.',
    });
  }

  if (data.is_airdrop_scam) {
    rows.push({
      id: 'is_airdrop_scam',
      label: 'Airdrop Scam',
      value: toBoolean(data.is_airdrop_scam),
      type: 'boolean',
      tooltip: 'Indicates whether the token is part of an airdrop scam.',
    });
  }

  if (data.trust_list) {
    rows.push({
      id: 'trust_list',
      label: 'Trust List',
      value: toBoolean(data.trust_list),
      type: 'boolean',
      tooltip: 'Indicates whether the token is on a list of trusted tokens.',
    });
  }

  if (data.is_open_source) {
    rows.push({
      id: 'is_open_source',
      label: 'Open Source',
      value: toBoolean(data.is_open_source),
      type: 'boolean',
      tooltip: `Indicates if the token's source code is openly available.`,
    });
  }

  if (data.is_whitelisted) {
    rows.push({
      id: 'is_whitelisted',
      label: 'Whitelisted',
      value: toBoolean(data.is_whitelisted),
      type: 'boolean',
      tooltip: 'Indicates whether the token is on a whitelist.',
    });
  }

  if (data.other_potential_risks) {
    rows.push({
      id: 'other_potential_risks',
      label: 'Other Potential Risks',
      value: toBoolean(data.other_potential_risks),
      type: 'boolean',
      tooltip:
        'Indicates if there are other potential risks associated with the token.',
    });
  }

  if (data.note) {
    rows.push({
      id: 'note',
      label: 'Note',
      value: data.note,
      type: 'string',
      tooltip: 'Additional notes or comments about the token.',
    });
  }

  // Holders info
  data.holders?.slice(0, 3).forEach((holder, index) => {
    rows.push({
      id: `holder_percent_${index}`,
      label: `Holder ${index + 1} Percent`,
      value: Number(holder.percent) * 100,
      type: 'number',
      percent: true,
      tooltip: `Percentage of total tokens held by holder ${index + 1}.`,
    });

    rows.push({
      id: `holder_balance_${index}`,
      label: `Holder ${index + 1} Balance`,
      value: Number(holder.balance),
      type: 'number',
      tooltip: `Balance in tokens held by holder ${index + 1}.`,
    });

    rows.push({
      id: `holder_address_${index}`,
      label: `Holder ${index + 1} Address`,
      value: holder.address,
      type: 'wallet_address',
      tooltip: 'Ethereum address of holder ' + (index + 1),
    });
  });

  return rows;
};

export { getCexPoolRows, getDexPoolRows, getScoreRows, getGoPlusSecurityRows };
